import React, {useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef} from 'react'; // NATIVE A11Y CHANGE: useRef is needed to manage read-only focus targets without changing DOM structure/styles.
import {AccordionDetails, Box, Typography, Divider} from '@mui/material';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import ArrRadio from '../../../../../components/common/arr-radio/ArrRadio';
import {useTranslation, Trans} from 'react-i18next';
import FileTypeSelector from '../../../../common/arr-fileTypeSelector/FileTypeSelector';
import './NativeComponent.scss';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import TagsTreePanel from '../../common/tags-tree-panel/TagsTreePanel';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {FileTypeItem} from '../../../../../api/types/production.type';
import {
  useGetNativeComponent,
  useGetTemplateNativeComponent,
} from '../../../../../hooks/productions/useProductions';
import {ModuleType, FormMode} from '../../CreateNewProductionUtils';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {setIsFieldsChanged} from '../../../../../redux/slice/productionSlice';

interface NativeComponentProps {
  fileGroupList?: FileTypeItem[];
  tagTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  productionId: number;
  isProductionSet: boolean;
  onFileGroupsAndTagsChange?: (fileGroups: string, tags: string) => void;
  onNativeDirtyChange?: (isDirty: boolean) => void;
  onNativeBaselineReady?: () => void;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
}

export const NativeComponent: React.FC<NativeComponentProps> = ({
  fileGroupList = [],
  tagTreeData = [],
  productionId,
  isProductionSet,
  onFileGroupsAndTagsChange,
  onNativeDirtyChange,
  onNativeBaselineReady,
  mode = FormMode.CREATE,
  isTemplate = false,
  templateId,
}) => {
  const {t} = useTranslation();
  const isViewMode = mode === FormMode.VIEW;

  const options = useMemo(
    () => [
      {
        label: t(
          'productionsAndExports.createNewProduction.productionComponents.native.parentsOfPrivilegedAndRedacted'
        ),
        value: '0',
      },
      {
        label: t(
          'productionsAndExports.createNewProduction.productionComponents.native.completeFamiliesOfPrivilegedAndRedacted'
        ),
        value: '1',
      },
    ],
    [t]
  );

  // Get Redux state
  const selectedSecurityGroupId = useSelector(
    (state: RootState) => state.production.securityGroupId
  );

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  // API call to get native component data
  const {
    data: nativeComponentDataFromProduction,
    isLoading: isNativeComponentLoadingFromProduction,
    error: nativeComponentErrorFromProduction,
  } = useGetNativeComponent(
    {
      SecurityGroupId: selectedSecurityGroupId,
      moduletype: moduleType,
      productionId: productionId,
      exportProductionId: 0,
    },
    !!selectedSecurityGroupId && !isTemplate && productionId > 0
  );

  const {
    data: nativeComponentDataFromTemplate,
    isLoading: isNativeComponentLoadingFromTemplate,
    error: nativeComponentErrorFromTemplate,
  } = useGetTemplateNativeComponent(
    {
      templateId: templateId!,
      securityGroupId: selectedSecurityGroupId,
    },
    !!selectedSecurityGroupId && isTemplate && !!templateId
  );

  // Use the appropriate data based on isTemplate flag
  const nativeComponentData = isTemplate
    ? nativeComponentDataFromTemplate
    : nativeComponentDataFromProduction;
  const isNativeComponentLoading = isTemplate
    ? isNativeComponentLoadingFromTemplate
    : isNativeComponentLoadingFromProduction;
  const nativeComponentError = isTemplate
    ? nativeComponentErrorFromTemplate
    : nativeComponentErrorFromProduction;
  const dispatch = useDispatch();

  const [generateNative, setGenerateNative] = useState(false);
  const [exportNative, setExportNative] = useState(false);
  const [selected, setSelected] = useState('');

  const [isTagsFlyoutOpen, setIsTagsFlyoutOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedObjects, setSelectedObjects] = useState<
    ExtendedTreeItemProps[]
  >([]);
  const [selectedFileTypeIds, setSelectedFileTypeIds] = useState<string[]>([]);
  const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);
  const previousSelectedIdsRef = useRef<string[]>([]);
  const initialNativeConfigRef = useRef<string>('');
  const isBaselineReadyRef = useRef(false);
  const baselineReadyNotifiedRef = useRef(false);

  const getInitialFileTypeIds = useCallback((): string[] => {
    if (nativeComponentData?.nativeSelectedFileGroups) {
      return nativeComponentData.nativeSelectedFileGroups
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }

    return fileGroupList
      .filter((item) => item.isFileTypeSelected)
      .map((item) => item.fileTypeID.toString());
  }, [fileGroupList, nativeComponentData?.nativeSelectedFileGroups]);

  const buildNativeSnapshot = (
    fileTypeIds: string[],
    tagIds: string[],
    produceLoadFile: boolean,
    produceNativeForTechDocs: boolean,
    familyValue: string
  ) =>
    JSON.stringify({
      fileTypes: [...fileTypeIds].sort(),
      tags: [...tagIds].map((x) => String(x)).sort(),
      produceLoadFile,
      produceNativeForTechDocs,
      familyValue: familyValue || '0',
    });

  const computeNativeDirty = (
    fileTypeIds = selectedFileTypeIds,
    tagIds = selectedObjects.map((obj) => String(obj.id)),
    produceLoadFile = generateNative,
    produceNativeForTechDocs = exportNative,
    familyValue = selected === '1' ? '1' : '0'
  ) =>
    Boolean(initialNativeConfigRef.current) &&
    buildNativeSnapshot(
      fileTypeIds,
      tagIds,
      produceLoadFile,
      produceNativeForTechDocs,
      familyValue
    ) !== initialNativeConfigRef.current;

  const updateNativeDirty = (
    fileTypeIds = selectedFileTypeIds,
    tagIds = selectedObjects.map((obj) => String(obj.id)),
    produceLoadFile = generateNative,
    produceNativeForTechDocs = exportNative,
    familyValue = selected === '1' ? '1' : '0'
  ) => {
    if (!isBaselineReadyRef.current) {
      return;
    }

    const dirty = computeNativeDirty(
      fileTypeIds,
      tagIds,
      produceLoadFile,
      produceNativeForTechDocs,
      familyValue
    );

    setComponentIsDirty((prev) => {
      if (prev === dirty) {
        return prev;
      }

      onNativeDirtyChange?.(dirty);
      return dirty;
    });

    if (dirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  };

  // Function to get current Native configuration
  const getCurrentConfig = () => {
    // Get selected file type IDs as comma-separated string from state
    const selectedFileTypeIdsString = selectedFileTypeIds.join(',');

    // Get selected tag IDs as comma-separated string
    const selectedTagIds = selectedObjects.map((obj) => obj.id).join(',');

    return {
      isSelectAllFileTypes: false, // TODO: Extract from Native component checkbox
      produceLoadFile: generateNative,
      generateHTMLforEmails: false, // TODO: Extract from Native component checkbox
      produceNativeForTechDocs: exportNative,
      producedLockedFiles: false, // TODO: Extract from Native component checkbox
      protectionOnExcelFile: false, // TODO: Extract from Native component checkbox
      nativeComponentmodel: '', // TODO: Extract from Native component input
      nativeSelectedFileGroups: selectedFileTypeIdsString
        ? `,${selectedFileTypeIdsString}`
        : '',
      nativeSelectedTags: selectedTagIds,
      eRedactedAndPrevilededFamily:
        selected === '0' ? 0 : selected === '1' ? 1 : 0,
    };
  };

  // Expose getCurrentConfig function globally for parent to access
  (window as any).getNativeConfig = getCurrentConfig;

  // Hydrate from API and establish baseline before paint so mount callbacks are not treated as edits.
  useLayoutEffect(() => {
    if (!fileGroupList?.length) {
      isBaselineReadyRef.current = false;
      baselineReadyNotifiedRef.current = false;
      return;
    }

    if (!nativeComponentData) {
      const preSelected = fileGroupList
        .filter((item) => item.isFileTypeSelected)
        .map((item) => item.fileTypeID.toString());
      setSelectedFileTypeIds(preSelected);
      previousSelectedIdsRef.current = preSelected;
      initialNativeConfigRef.current = buildNativeSnapshot(
        preSelected,
        [],
        false,
        false,
        '0'
      );
      isBaselineReadyRef.current = false;
      baselineReadyNotifiedRef.current = false;
      return;
    }

    const initialFileTypeIds = getInitialFileTypeIds();
    const initialTagIds = (nativeComponentData.nativeSelectedTags || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const initialFamilyValue =
      nativeComponentData.eRedactedAndPrevilededFamily === 1 ? '1' : '0';

    if (initialTagIds.length > 0 && tagTreeData.length === 0) {
      isBaselineReadyRef.current = false;
      return;
    }

    setGenerateNative(nativeComponentData.produceLoadFile || false);
    setExportNative(nativeComponentData.produceNativeForTechDocs || false);
    setSelected(initialFamilyValue);
    setSelectedFileTypeIds(initialFileTypeIds);
    previousSelectedIdsRef.current = initialFileTypeIds;

    if (initialTagIds.length > 0) {
      setSelectedIds(initialTagIds);

      const flattenTree = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): ExtendedTreeItemProps[] =>
        nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
          acc.push(node);
          if (node.children) acc.push(...flattenTree(node.children));
          return acc;
        }, []);

      const flatTree = flattenTree(tagTreeData);
      setSelectedObjects(
        flatTree.filter((node) => initialTagIds.includes(node.id))
      );
    } else {
      setSelectedIds([]);
      setSelectedObjects([]);
    }

    initialNativeConfigRef.current = buildNativeSnapshot(
      initialFileTypeIds,
      initialTagIds,
      Boolean(nativeComponentData.produceLoadFile),
      Boolean(nativeComponentData.produceNativeForTechDocs),
      initialFamilyValue
    );

    isBaselineReadyRef.current = false;
    baselineReadyNotifiedRef.current = false;
    setComponentIsDirty(false);
  }, [
    nativeComponentData,
    fileGroupList,
    tagTreeData,
    getInitialFileTypeIds,
  ]);

  useEffect(() => {
    if (!initialNativeConfigRef.current) {
      return;
    }

    const finalizeBaseline = () => {
      if (!initialNativeConfigRef.current) {
        return;
      }

      const dirty = nativeComponentData ? computeNativeDirty() : false;

      if (!isBaselineReadyRef.current) {
        isBaselineReadyRef.current = true;
        setComponentIsDirty(dirty);

        if (dirty) {
          baselineReadyNotifiedRef.current = true;
          onNativeDirtyChange?.(true);
          dispatch(setIsFieldsChanged({isFieldsChanged: true}));
        } else {
          baselineReadyNotifiedRef.current = true;
          onNativeDirtyChange?.(false);
          onNativeBaselineReady?.();
        }
        return;
      }

      if (nativeComponentData) {
        updateNativeDirty();
      }
    };

    // Defer until after FileTypeSelector mount effects (which may emit [] first).
    const frameId = window.requestAnimationFrame(finalizeBaseline);
    return () => window.cancelAnimationFrame(frameId);
  }, [
    nativeComponentData,
    selectedFileTypeIds,
    selectedObjects,
    generateNative,
    exportNative,
    selected,
    onNativeDirtyChange,
    onNativeBaselineReady,
  ]);

  // Notify parent when file groups or tags change (for form validation)
  // This follows the same pattern as DAT's onFieldsChange
  useEffect(() => {
    if (onFileGroupsAndTagsChange) {
      const selectedFileTypeIdsString = selectedFileTypeIds.join(',');
      const fileGroups = selectedFileTypeIdsString
        ? `,${selectedFileTypeIdsString}`
        : '';
      const tags = selectedObjects.map((obj) => obj.id).join(',');
      onFileGroupsAndTagsChange(fileGroups, tags);
    }
  }, [selectedFileTypeIds, selectedObjects, onFileGroupsAndTagsChange]);

  const handleToggleChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  checked: boolean
) => {
  if (isViewMode) return;
  setGenerateNative(checked);
  updateNativeDirty(
    selectedFileTypeIds,
    selectedObjects.map((obj) => String(obj.id)),
    checked,
    exportNative,
    selected
  );
};
  const handleExportChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  checked: boolean
) => {
  if (isViewMode) return;
  setExportNative(checked);
  updateNativeDirty(
    selectedFileTypeIds,
    selectedObjects.map((obj) => String(obj.id)),
    generateNative,
    checked,
    selected
  );
};

  // Tags are now managed through selectedObjects from API response

  const handleTags = (data: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    if (isViewMode) return;
    const selectedIds = data.map((item) => item.id);
    setSelectedIds(selectedIds);

    const flattenTree = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ): ExtendedTreeItemProps[] => {
      return nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
        acc.push(node);
        if (node.children) acc.push(...flattenTree(node.children));
        return acc;
      }, []);
    };

    const flatTree = flattenTree(tagTreeData);
    const selectedObjects = flatTree.filter((node) =>
      selectedIds.includes(node.id)
    );

    setSelectedObjects(selectedObjects);
    updateNativeDirty(
      selectedFileTypeIds,
      selectedObjects.map((obj) => String(obj.id)),
      generateNative,
      exportNative,
      selected
    );
  };

  const handleRemove = (value: string, type: 'id' | 'label' = 'id') => {
      if (isViewMode) return;
      const updated = selectedObjects.filter((item) =>
      type === 'id' ? item.id !== value : item.label !== value
    );
    setSelectedObjects(updated);
    // Also update selectedIds to keep them in sync
    const updatedIds = updated.map((obj) => obj.id);
    setSelectedIds(updatedIds);
    updateNativeDirty(
      selectedFileTypeIds,
      updated.map((obj) => String(obj.id)),
      generateNative,
      exportNative,
      selected
    );
  };

  // Handle file type selection changes

  // Memoizing handleFileTypeSelectionChange
  const handleFileTypeSelectionChange = useCallback(
    (newSelectedIds: string[]) => {
      if (isViewMode) {
        return;
      }

      // Block FileTypeSelector's initial empty notification before hydration settles.
      if (!isBaselineReadyRef.current && newSelectedIds.length === 0) {
        return;
      }

      const normalizeIds = (ids: string[]) => [...ids].sort().join(',');
      const isSameSelection =
        normalizeIds(newSelectedIds) ===
        normalizeIds(previousSelectedIdsRef.current);

      if (isSameSelection) {
        return;
      }

      previousSelectedIdsRef.current = newSelectedIds;
      setSelectedFileTypeIds(newSelectedIds);
      updateNativeDirty(newSelectedIds);
    },
    [isViewMode]
  );

  // Use fileGroupList from props (from getProductionComponents API) and apply selections from nativeComponentData
  const availableFileTypes = useMemo(() => {
    // Use fileGroupList from props as the base file types
    const baseFileTypes = fileGroupList;

    // If we have nativeSelectedFileGroups from getNativeComponent API, update the isFileTypeSelected property
    if (nativeComponentData?.nativeSelectedFileGroups) {
      const selectedIds = nativeComponentData.nativeSelectedFileGroups
        .split(',')
        .map((id) => id.trim());
      return baseFileTypes.map((fileType) => ({
        ...fileType,
        isFileTypeSelected: selectedIds.includes(
          fileType.fileTypeID.toString()
        ),
      }));
    }

    return baseFileTypes;
  }, [nativeComponentData, fileGroupList]);

  // Show loading state
  if (isNativeComponentLoading) {
    return (
      <Box className="native-component">
        <Typography
          data-testid={`native-component-loading-native-component-data`}
        >
          Loading native component data...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (nativeComponentError) {
    return (
      <Box className="native-component">
        <Typography
          color="error"
          data-testid={`native-component-error-loading-native-component-data`}
        >
          Error loading native component data: {nativeComponentError.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="native-component native-accordion"
      data-test-id={'NativeAccordion'}
    >
      <AccordionDetails>
        <div
          className="produce-native-files"
          data-testid={`native-component-produce-native-file-produce-native-description-produce-native-description1`}
        >
          <div
            className="produce-header"
            data-testid={`native-component-produce-native-file`}
          >
            <Typography
              variant="subtitle1"
              className="produce-title"
              data-testid={`native-component-produce-native-file-text`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponent.produceNativeFile'
              )}
            </Typography>
            <ArrTooltipWrapper
              title="Produce Native Files"
              trigger={
                <InfoIcon
                  aria-label="Info"
                  className="view-user-info-icon"
                  tabIndex={0}
                />
              }
            >
              <Trans
                i18nKey="productionsAndExports.createNewProduction.productionComponents.native.nativeDocumentSpecificationDescription"
                components={{br: <br data-testid={`native-component-br`} />}}
              />
            </ArrTooltipWrapper>
          </div>
          <Box className="produce-description">
            <Typography
              className="typo-text body3_size"
              data-testid={`native-component-produce-native-description`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponent.produceNativeDescription'
              )}
            </Typography>

            <Typography
              className="typo-text body3_size"
              data-testid={`native-component-produce-native-description1`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponent.produceNativeDescription1'
              )}
            </Typography>
          </Box>
        </div>
        <Divider className="native-divider" />
        <div
          className="tag-files"
          data-testid={`native-component-select-file-tags`}
        >
          <Typography
            variant="subtitle1"
            className=" heading typo-text subhead4_size"
            data-testid={`native-component-select-file-tags-text`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.native.selectFileTags'
            )}
          </Typography>
        </div>
        <Box className="native-filetag-container">
          <Box className="native-fileType">
            <FileTypeSelector
              fileGroupList={availableFileTypes}
              onSelectionChange={handleFileTypeSelectionChange}
              disabled={isViewMode}
              checkboxDataTestId="native-component-file-type-selector"
            />
          </Box>

          <Box className="native-fileType">
            <div
              className="filetype-selector"
              data-testid={`native-component-tags-add-tags`}
            >
              <div
                className="filetype-header"
                data-testid={`native-component-tags-add-tags-btn`}
              >
                <Typography
                  className="typo-text subhead5_size filetype-title"
                  data-testid={`native-component-tags`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponent.tags'
                  )}
                </Typography>
                <ArrButton
                  variant="primary"
                  size="small"
                  dataTestId="native-component-add-tags-button"
                  onClick={() => !isViewMode && setIsTagsFlyoutOpen(true)}
                  disabled={isViewMode}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponent.addTags'
                  )}
                </ArrButton>
              </div>

              <div
                className="selected-types"
                data-testid={`native-component-div`}
              >
                {selectedObjects.map((item, index) => {
                  return (
                    <ArrFilterChip
                      key={item.id}
                      classNames="chip"
                      filterName=""
                      selections={[{id: item.id, value: item.label}]}
                      variant="v1"
                      onRemove={() => handleRemove(item.id)}
                      disabled={isViewMode}
                      dataTestId={`selected-types-${index}`}
                    />
                  );
                })}
              </div>
            </div>
            {isTagsFlyoutOpen && (
              <TagsTreePanel
                title={t(
                  'productionsAndExports.createNewProduction.datComponent.selectTags'
                )}
                type="flyout"
                tags={tagTreeData}
                selectedTags={selectedObjects}
                isOpen={isTagsFlyoutOpen}
                onClose={() => setIsTagsFlyoutOpen(false)}
                setSelectedTags={(items) => {
                  handleTags(items);
                }}
              />
            )}
          </Box>
        </Box>

        <Divider className="native-divider" />

        <Box className="native-section">
          <Typography
            variant="subtitle1"
            className="typo-text subhead4_size section-title"
            data-testid={`native-component-dont-produced-native`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.native.dontProducedNative'
            )}
          </Typography>
          <ArrTooltipWrapper
            title="Privileged and Redacted Document Family Members"
            trigger={<InfoIcon className="view-user-info-icon" tabIndex={0} />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.native.dontProducedNativeDescription"
              components={{
                br: <br data-testid={`native-component-br-btn`} />,
                strong: <strong data-testid={`native-component-strong`} />,
                p: <p data-testid={`native-component-p`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>

        <Box className="native-radio-group">
          <ArrRadio
            dataTestId="controlled-radio-buttons-group"
            size="small"
            name="controlled-radio-buttons-group"
            onChange={(value) => {
              if (isViewMode) return;
              setSelected(value);
              updateNativeDirty(
                selectedFileTypeIds,
                selectedObjects.map((obj) => String(obj.id)),
                generateNative,
                exportNative,
                value
              );
            }}
            value={selected}
            data={options}
            className="section-title custom-label"
            disabled={isViewMode}
          />
        </Box>
        <Box className="produce-description native-note">
          <Typography
            className="typo-text body3_size"
            data-testid={`native-component-note-note-content`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponent.note'
            )}
            :{' '}
            {t(
              'productionsAndExports.createNewProduction.productionComponent.noteContent'
            )}
          </Typography>
        </Box>

        <Box className="native-toggle">
          <ArrToggle
            disabled={isViewMode}
            checked={exportNative}
            onChange={handleExportChange}
            dataTestId="chkNoNativeForTechIssueTag"
          />
          <Typography
            className="toggle-text typo-text subhead5_size"
            data-testid={`native-component-dont-export-native`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.native.dontExportNative'
            )}
          </Typography>
          <ArrTooltipWrapper
            title="Enable Placeholders by Selecting File Types"
            trigger={<InfoIcon className="view-user-info-icon" tabIndex={0} />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.native.nativePlaceholdersByFileTypeDescription"
              components={{
                br: <br data-testid={`native-component-br-button`} />,
                strong: <strong data-testid={`native-component-strong-btn`} />,
                p: <p data-testid={`native-component-p-text`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>

        <Divider className="native-divider" />

        <Box className="native-toggle">
          <ArrToggle
            disabled={isViewMode}
            checked={generateNative}
            onChange={handleToggleChange}
            dataTestId="native-toggle"
          />
          <Typography
            className="toggle-text typo-text subhead5_size"
            data-testid={`native-component-generate-native`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.native.generateNative'
            )}
          </Typography>
          <ArrTooltipWrapper
            title="Generate Load File"
            trigger={<InfoIcon className="view-user-info-icon" tabIndex={0} />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.native.generateNativeDescription"
              components={{
                br: <br data-testid={`native-component-br-icon`} />,
                strong: (
                  <strong data-testid={`native-component-strong-button`} />
                ),
                p: <p data-testid={`native-component-p-btn`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>
      </AccordionDetails>
    </Box>
  );
};
